import type {
  Trim,
  TrimDetailViewProps,
  VehicleTrimDetailTableRow
} from "@dealer-center/inventory-pages-gvm-interface";
import React, { useEffect, useMemo } from "react";
import type {
  InterstateOnChangeEvent,
  SelectInputEventValue,
  SelectInputOption,
  TypeSafeTableColumns
} from "@dealer-center/core-components";
import { SelectInput, Table } from "@dealer-center/core-components";
import { PropertyOverride } from "../../Common";
import { trimMetadata } from "@dealer-center/inventory-core-utils";
import { useIsGvmRwAccessUser } from "../../util/gvmUserUtil";
import { hasChanges } from "../../util/trimDataMapper";

const modelOverrideFieldName: keyof Trim = "overrideVehicleId";

function modelOverrideData(
  availableModels: Record<string, string>,
  originalVehicleId: string,
  overrideVehicleId: string
) {
  const data: {
    options: SelectInputOption[];
    defaultValue: string | undefined;
  } = {
    options: [],
    defaultValue: undefined
  };
  let index = 0;
  for (const [vehicleName, vehicleId] of Object.entries(availableModels)) {
    const label =
      vehicleId === originalVehicleId
        ? `${vehicleName} (Default)`
        : vehicleName;
    const value = `${vehicleId};${index++}`;

    data.options.push({ label, value });
    if (vehicleId === overrideVehicleId && !data.defaultValue)
      data.defaultValue = value;
  }

  return data;
}

export const TrimDetailView = ({
  isLoading,
  trimViewData,
  inputRefs,
  inputKeys,
  availableModels,
  setSaveButtonEnabled
}: TrimDetailViewProps) => {
  const isGvmRwAccessUser = useIsGvmRwAccessUser();
  const modelData = modelOverrideData(
    availableModels,
    trimViewData.originalItem.globalVehicleId,
    trimViewData.editItem.overrideVehicleId
  );

  const trimDetailColumns =
    useMemo((): TypeSafeTableColumns<VehicleTrimDetailTableRow> => {
      return [
        {
          key: "property",
          dataIndex: "property",
          title: "",
          columnSortable: false,
          width: 200
        },
        {
          key: "sourceItem",
          dataIndex: "sourceItem",
          title: trimViewData.viewItem.thirdPartyId,
          columnSortable: false
        },
        {
          key: "override",
          dataIndex: "overrideInput",
          title:
            (trimViewData.qa ? "QA Item" : "Live Item") +
            " (" +
            trimViewData.editItem.thirdPartyId +
            ")",
          columnSortable: false
        },
        {
          key: "overrideSource",
          dataIndex: "overrideSource",
          title: "",
          columnSortable: false,
          width: 200
        }
      ];
    }, [
      trimViewData.viewItem.thirdPartyId,
      trimViewData.qa,
      trimViewData.editItem.thirdPartyId
    ]);

  useEffect(() => {
    inputRefs.current[modelOverrideFieldName] =
      trimViewData.editItem.overrideVehicleId;
  }, [trimViewData.editItem.overrideVehicleId]);

  const renderInput = (field: string, viewValue: string, editValue: string) => {
    return (
      <PropertyOverride
        editItem={trimViewData.editItem}
        field={field}
        initialValue={editValue}
        inputRefs={inputRefs}
        isRequired={false}
        key={inputKeys[field]}
        maxLength={trimMetadata.length(field, 20)}
        resetValue={viewValue}
        setSaveButtonEnabled={setSaveButtonEnabled}
        type={trimMetadata.isNumeric(field) ? "number" : "text"}
      />
    );
  };

  const getTrimDetailData = (): VehicleTrimDetailTableRow[] => {
    const tableRows: VehicleTrimDetailTableRow[] = [];

    const originalItem = trimViewData.originalItem;
    const editItem = trimViewData.editItem;
    const viewItem = trimViewData.viewItem;
    const isQaRecordPresent = trimViewData.qa;

    for (const key in trimViewData.validFields) {
      let overrideSource = "";
      const viewValue = String(viewItem[key as keyof Trim] ?? "");
      const editValue = String(editItem[key as keyof Trim] ?? "");
      const originalValue = String(originalItem[key as keyof Trim] ?? "");

      if (
        isQaRecordPresent &&
        (originalValue !== viewValue || originalItem === viewItem)
      ) {
        overrideSource = "QA Change";
      } else if (originalValue !== editValue) {
        overrideSource = "DDC Change";
      }

      tableRows.push({
        property: trimViewData.validFields[key],
        sourceItem: viewValue,
        overrideInput: renderInput(key, viewValue, editValue),
        overrideSource
      });
    }

    return tableRows;
  };

  function enableButton() {
    const enabled =
      isGvmRwAccessUser && hasChanges(inputRefs, trimViewData.editItem);
    setSaveButtonEnabled(enabled);
  }

  const handleModelChange = (
    e: InterstateOnChangeEvent<SelectInputEventValue>
  ) => {
    inputRefs.current[e.target.name ?? ""] =
      e.target.value?.split(";")[0] ?? "";
    enableButton();
  };

  const render = () => {
    return (
      <>
        <SelectInput
          disabled={!isGvmRwAccessUser}
          displayDeselectOption={false}
          label="Model Override:"
          name={modelOverrideFieldName}
          options={modelData.options}
          sx={{ marginBottom: "20px" }}
          value={modelData.defaultValue}
          onChange={handleModelChange}
        />
        <Table.Table
          columns={trimDetailColumns}
          data={getTrimDetailData()}
          data-testid={"gvm-trim-detail-view"}
          dataDensity="small"
          enablePagination={false}
          isLoading={isLoading}
          scrollY={500}
          sortableColumns={false}
          tableLayout={"auto"}
        />
      </>
    );
  };

  return render() || null;
};
